import { useCallback, useEffect, useState } from 'react';
import { enviosCampanaService, type EnvioCampanaConDetalles } from '../../services/enviosCampanaService';
import type { EnvioCampanaAsignacion, EnvioCampanaHistorial, OwnerFondoCampana } from '../../types/fondosCampana';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { EnvioCampanaOperacionModal } from '../EnvioCampanaOperacionModal';
import { ComprobanteDeTransferencia } from '../comprobantes/ComprobanteDeTransferencia';
import { Button } from '../ui/Button';
import { FormErrorAlert } from '../ui/FormErrorAlert';
import { accionesEnvioDisponibles, ETIQUETAS_ACCION, ETIQUETAS_ESTADO, motivoHistorial, type AccionEnvioCampana } from './accionesEnvio';
interface Props {
  readonly envio: EnvioCampanaConDetalles; readonly owner: OwnerFondoCampana;
  readonly puedeOperar: boolean; readonly onRefresh: () => void; readonly onRemitir: (masterId: string) => void;
}
export function EnvioCampanaFila({ envio, owner, puedeOperar, onRefresh, onRemitir }: Props) {
  const [historial, setHistorial] = useState<EnvioCampanaHistorial[]>([]);
  const [asignaciones, setAsignaciones] = useState<EnvioCampanaAsignacion[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accion, setAccion] = useState<AccionEnvioCampana | null>(null);
  const consultar = useCallback(() => Promise.all([
        enviosCampanaService.getHistorial(envio.id),
        envio.origen_diocesis_id ? enviosCampanaService.getAsignaciones(envio.id) : Promise.resolve([]),
      ]), [envio.id, envio.origen_diocesis_id]);
  const recibir = useCallback(([eventos, fuentes]: [EnvioCampanaHistorial[], EnvioCampanaAsignacion[]]) => {
      setHistorial(eventos); setAsignaciones(fuentes);
      setError(null); setCargando(false);
  }, []);
  const fallo = useCallback(() => {
    setError('No pudimos consultar la historia de este envío.'); setCargando(false);
  }, []);
  useEffect(() => {
    let vigente = true;
    consultar().then(datos => { if (vigente) recibir(datos); }).catch(() => { if (vigente) fallo(); });
    return () => { vigente = false; };
  }, [consultar, recibir, fallo, envio.revision]);
  const cargar = () => { setCargando(true); void consultar().then(recibir).catch(fallo); };
  const acciones = accionesEnvioDisponibles(envio, owner, puedeOperar && !cargando && !error);
  const remitir = puedeOperar && owner.tipo === 'diocesis' && owner.id === envio.destino_diocesis_id
    && ['nacional', 'global'].includes(envio.campana_nivel_origen ?? '')
    && envio.estado === 'confirmado' && !!envio.lumisial_id && !cargando && !error;
  return <article className="rounded-xl border border-slate-200 p-4 space-y-3">
    <div className="flex flex-wrap justify-between gap-3"><div>
      <h4 className="font-semibold text-slate-800">{envio.campana_master_nombre ?? envio.campana_nombre ?? 'Campaña'}</h4>
      <p className="text-sm text-slate-600">{envio.lumisial_nombre ?? envio.origen_diocesis_nombre ?? 'Junta Administrativa remitente'}
        {' → '}{envio.destino_diocesis_nombre ?? envio.destino_provincia_nombre ?? 'Junta Administrativa receptora'}</p>
    </div><p className="font-semibold">{formatCurrency(envio.monto)}</p></div>
    <p className="text-sm">{ETIQUETAS_ESTADO[envio.estado]} · {formatDate(envio.fecha_envio)} · revisión {envio.revision}</p>
    {envio.descripcion && <p className="text-sm text-slate-600">{envio.descripcion}</p>}
    <div className="flex items-center gap-2 text-sm">
      <span className="text-slate-500">Transferencia</span>
      <ComprobanteDeTransferencia comprobante={envio.comprobante ?? null} />
    </div>
    {envio.estado === 'observado' && <p className="text-sm text-amber-800">El dinero sigue enviado y no está disponible. Corrige la misma rendición.</p>}
    {cargando && <output className="block text-sm">Consultando historia…</output>}
    <FormErrorAlert error={error} />
    {error && <Button variant="secondary" onClick={cargar}>Reintentar consulta</Button>}
    <details><summary className="cursor-pointer text-sm font-medium">Historia y procedencia</summary>
      <div className="space-y-2 mt-3 text-sm">
        {asignaciones.length > 0 && <p>Esta remisión reúne {asignaciones.length} recepciones; total asignado {formatCurrency(asignaciones.reduce((s, a) => s + a.monto_asignado, 0))}.</p>}
        {!cargando && !error && historial.length === 0 && <p>Sin eventos de rendición registrados.</p>}
        {historial.map(evento => <p key={evento.solicitud_id}>{formatDate(evento.created_at)} · {evento.accion.replace(/_/g, ' ')}
          {motivoHistorial(evento) && <> · {motivoHistorial(evento)}</>}</p>)}
      </div>
    </details>
    <div className="flex flex-wrap gap-2">
      {acciones.map(a => <Button key={a} variant="secondary" size="sm" onClick={() => setAccion(a)}>{ETIQUETAS_ACCION[a]}</Button>)}
      {remitir && envio.campana_master_id && <Button size="sm" onClick={() => onRemitir(envio.campana_master_id!)}>Remitir a JAN</Button>}
    </div>
    {accion && <EnvioCampanaOperacionModal envio={envio} owner={owner} readOnly={!puedeOperar} accion={accion}
      onClose={() => setAccion(null)} onSuccess={onRefresh} />}
  </article>;
}
